import { Capacitor } from '@capacitor/core';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EntryRedirect } from './components/auth/entry-redirect';
import { ForceUpdateScreen } from './components/force-update-screen';
import { useAuth } from './hooks/use-auth';
import { useShellContext } from './contexts/shell-context';
import { useVersionGate } from './hooks/use-version-gate';
import { currentBuild, gatePlatform } from './lib/build-info';
import { LoginPage } from './pages/login';
import { LegalReacceptanceGate } from './components/legal/legal-reacceptance-gate';
import { RedirectIfAuthenticated, RequireBusinessContext } from './lib/auth/session-guard';
import { ConsumerShell } from './shells/consumer';
const LandingPage = lazy(() => import('./pages/landing').then((module) => ({ default: module.LandingPage })));
const AuthCallbackPage = lazy(() => import('./pages/auth/auth-callback').then((module) => ({ default: module.AuthCallbackPage })));
const WelcomePage = lazy(() => import('./pages/auth/welcome').then((module) => ({ default: module.WelcomePage })));
const SignupPage = lazy(() => import('./pages/auth/signup').then((module) => ({ default: module.SignupPage })));
const PasswordRecoveryPage = lazy(() => import('./pages/auth/password-recovery').then((module) => ({ default: module.PasswordRecoveryPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/reset-password').then((module) => ({ default: module.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/verify-email').then((module) => ({ default: module.VerifyEmailPage })));
const ConsumerOnboardingPage = lazy(() => import('./pages/onboarding/consumer').then((module) => ({ default: module.ConsumerOnboardingPage })));
const LegalPage = lazy(() => import('./pages/legal/legal-page').then((module) => ({ default: module.LegalPage })));
const NotFoundPage = lazy(() => import('./pages/not-found').then((module) => ({ default: module.NotFoundPage })));
const ProfessionalShell = lazy(() => import('./shells/professional').then((module) => ({ default: module.ProfessionalShell })));
const ProfileSoonPage = lazy(() => import('./pages/discovery/profile-soon').then((module) => ({ default: module.ProfileSoonPage })));

function deferred(element: ReactNode, label: string) {
  return <Suspense fallback={<p role="status" aria-live="polite">{label}</p>}>{element}</Suspense>;
}

/**
 * Roteamento por plataforma (PRD §4.6.4).
 *
 * A diferença entre nativo e web não é cosmética: em nativo a **landing não existe** no bundle e
 * `/` cai direto no login. E `/admin` não resolve em superfície nenhuma — o painel vive em
 * `admin.powerfulconnect.pt` (§4.6.6). Deixar o shell administrativo alcançável dentro do app
 * nativo é rejeição na loja, não bug de navegação.
 *
 * `useAuth` é contrato: o M1 troca a implementação pela sessão real do Supabase, e este
 * componente não muda.
 */
export function AppRouter() {
  const { t } = useTranslation();
  const show = (element: ReactNode) => deferred(element, t('state.loading'));
  const isNative = Capacitor.isNativePlatform();
  const { isAuthenticated, isLoading = false, requiresEmailVerification = false } = useAuth();
  const { contexto, isLoading: isShellLoading } = useShellContext();

  // O gate corre antes de qualquer navegação e vale para as duas superfícies: um build que já
  // não consegue ler o schema não pode alcançar rota nenhuma (§10.15). Falha aberta — ver
  // `useVersionGate`.
  const platform = gatePlatform();
  const gate = useVersionGate({ platform, build: currentBuild() });

  if (gate.blocked) {
    return <ForceUpdateScreen platform={platform} messageKey={gate.messageKey} />;
  }

  if (isLoading || isShellLoading) {
    return <p role="status" aria-live="polite">{t('state.loading')}</p>;
  }

  // A escolha do shell vive aqui, e não em `use-auth`, para que o roteador dependa de UM único
  // export daquele módulo (`useAuth`). Menos superfície de acoplamento com o que o M1 vai
  // reescrever — e é a fronteira que o próprio teste desta fatia exercita ao mockar o hook.
  const shellFor = () =>
    contexto === 'professional' ? (
      show(<ProfessionalShell />)
    ) : (
      <ConsumerShell />
    );

  // App nativo: pula a landing, vai direto para auth/home.
  if (isNative) {
    if (!isAuthenticated) {
      return show(
        <Routes>
          <Route path="/legal/:kind" element={<LegalPage />} />
          <Route path="/welcome" element={<RedirectIfAuthenticated><WelcomePage /></RedirectIfAuthenticated>} />
          <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
          <Route path="/signup" element={<RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>} />
          <Route path="/password-recovery" element={<PasswordRecoveryPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/verify" element={<VerifyEmailPage />} />
          <Route path="/" element={requiresEmailVerification ? <Navigate to="/verify" replace /> : <LoginPage />} />
          {/* Tudo o mais converge para o login: em nativo não há superfície pública. */}
          <Route path="*" element={<LoginPage />} />
        </Routes>
      );
    }

    return show(
      <Routes>
        <Route path="/legal/:kind" element={<LegalPage />} />
        <Route path="/welcome" element={<Navigate to="/" replace />} />
        <Route path="/signup" element={<Navigate to="/" replace />} />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route path="/onboarding/consumer" element={<LegalReacceptanceGate><ConsumerOnboardingPage /></LegalReacceptanceGate>} />
        <Route path="/home/*" element={<LegalReacceptanceGate><ConsumerShell /></LegalReacceptanceGate>} />
        <Route path="/pro/:slug" element={<LegalReacceptanceGate><ProfileSoonPage /></LegalReacceptanceGate>} />
        <Route path="/dashboard/*" element={<RequireBusinessContext><LegalReacceptanceGate><ProfessionalShell /></LegalReacceptanceGate></RequireBusinessContext>} />
        {/* T1.19b: a entrada pós-login leva a conta nova ao onboarding que escolheu (ADR 0039). */}
        <Route path="/" element={<LegalReacceptanceGate><EntryRedirect>{shellFor()}</EntryRedirect></LegalReacceptanceGate>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    );
  }

  // Web: visitante não autenticado vê a landing.
  return show(
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <LegalReacceptanceGate>
              <EntryRedirect>{shellFor()}</EntryRedirect>
            </LegalReacceptanceGate>
          ) : requiresEmailVerification ? (
            <Navigate to="/verify" replace />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route path="/welcome" element={<RedirectIfAuthenticated><WelcomePage /></RedirectIfAuthenticated>} />
      <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
      <Route path="/signup" element={<RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>} />
      <Route path="/password-recovery" element={<PasswordRecoveryPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/verify" element={<VerifyEmailPage />} />
      <Route path="/legal/:kind" element={<LegalPage />} />
      {/*
        T1.19b: existia só no ramo nativo — na web caía no catch-all, até com sessão, e o onboarding
        consumer da T1.14 era inalcançável. Mesmo tratamento de sessão de `/home`.
      */}
      <Route
        path="/onboarding/consumer"
        element={
          isAuthenticated ? (
            <LegalReacceptanceGate>
              <ConsumerOnboardingPage />
            </LegalReacceptanceGate>
          ) : (
            <Navigate to={requiresEmailVerification ? '/verify' : '/login'} replace />
          )
        }
      />
      <Route
        path="/home/*"
        element={
          isAuthenticated ? (
            <LegalReacceptanceGate>
              <ConsumerShell />
            </LegalReacceptanceGate>
          ) : (
            <Navigate to={requiresEmailVerification ? '/verify' : '/login'} replace />
          )
        }
      />
      <Route
        path="/pro/:slug"
        element={isAuthenticated ? <LegalReacceptanceGate><ProfileSoonPage /></LegalReacceptanceGate> : <Navigate to={requiresEmailVerification ? '/verify' : '/login'} replace />}
      />
      <Route
        path="/dashboard/*"
        element={
          isAuthenticated ? (
            <RequireBusinessContext>
              <LegalReacceptanceGate>
                <ProfessionalShell />
              </LegalReacceptanceGate>
            </RequireBusinessContext>
          ) : (
            <Navigate to={requiresEmailVerification ? '/verify' : '/login'} replace />
          )
        }
      />
      {/* Sem rota /admin, por desenho — cai no catch-all como qualquer caminho inexistente. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
